// The wizard's memory between screens — module state like the session store.
// The Authentik flow lives in the Enrollment's client and must survive route
// changes; it is in-memory only (a reload sends the person back to the phone,
// the step that can be redone). Everything the person ANSWERED is persisted in
// localStorage (`erp.onboarding.draft`, HANDOFF §14.1: the draft is local
// before the phone is verified) so a reload keeps it; after the business is
// created the id is kept too, so a retry never creates twice.

import { useSyncExternalStore } from 'react';
import { createEnrollment, type Enrollment } from '@/lib/auth';
import { runtimeFlags } from '@/lib/env';
import { readJson, writeStore } from '@/lib/storage';
import type { BusinessDraft, BusinessType, ClaimedMembership, DayHours, PublicInvite, TeamInvite } from './data/api';

export type Intent = 'owner' | 'member';

/** Saturday-first week (locales.json IQ: week_start sat, weekend [fri]) → booking weekday (0 = Sunday … 6 = Saturday). */
export const WEEK = [
  { weekday: 6, name: 'السبت', short: 'س', weekend: false },
  { weekday: 0, name: 'الأحد', short: 'ح', weekend: false },
  { weekday: 1, name: 'الاثنين', short: 'ن', weekend: false },
  { weekday: 2, name: 'الثلاثاء', short: 'ث', weekend: false },
  { weekday: 3, name: 'الأربعاء', short: 'ر', weekend: false },
  { weekday: 4, name: 'الخميس', short: 'خ', weekend: false },
  { weekday: 5, name: 'الجمعة', short: 'ج', weekend: true },
] as const;

export interface DayDraft {
  open: boolean;
  /** 'HH:MM' (24 h). */
  from: string;
  to: string;
}

export interface HoursDraft {
  /** WEEK order (Saturday first). */
  days: DayDraft[];
}

/** The business app's starting week: Saturday–Thursday 9:00–21:00, Friday off. */
export const DEFAULT_HOURS: HoursDraft = {
  days: WEEK.map((d) => ({ open: !d.weekend, from: '09:00', to: '21:00' })),
};

export function toMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** The PUT /hours payload: open days only, each with its own range. */
export function hoursPayload(h: HoursDraft): DayHours[] {
  const out: DayHours[] = [];
  h.days.forEach((d, i) => {
    if (!d.open) return;
    const openMin = toMinutes(d.from);
    const closeMin = toMinutes(d.to);
    if (openMin == null || closeMin == null) return;
    out.push({ weekday: WEEK[i].weekday, openMin, closeMin });
  });
  return out;
}

/** Shape 3's work days (ruling R13): the whole day on every open day — periods only read HoursForDate(...).Open. */
export function workdaysPayload(h: HoursDraft): DayHours[] {
  const out: DayHours[] = [];
  h.days.forEach((d, i) => {
    if (d.open) out.push({ weekday: WEEK[i].weekday, openMin: 0, closeMin: 1440 });
  });
  return out;
}

/** The first day's range copied onto every OTHER open day (board: «نسخ إلى بقية الأيام»). */
export function copyFirstDay(h: HoursDraft): HoursDraft {
  const first = h.days[0];
  return { days: h.days.map((d, i) => (i === 0 || !d.open ? d : { ...d, from: first.from, to: first.to })) };
}

/**
 * The blueprint's business-pin-weak rule (infra 02-business.yaml), checked
 * before the round trip: one repeated digit, a straight run up or down (no
 * wrap), or a run of the account's own phone digits.
 */
export function isWeakEnrollPin(pin: string, phone: string): boolean {
  if (!/^\d{6}$/.test(pin)) return false;
  if (new Set(pin).size === 1) return true;
  const steps = new Set<number>();
  for (let i = 1; i < 6; i++) steps.add(pin.charCodeAt(i) - pin.charCodeAt(i - 1));
  if (steps.size === 1 && (steps.has(1) || steps.has(-1))) return true;
  const digits = phone.replace(/\D/g, '');
  return !!digits && digits.includes(pin);
}

export interface BranchesDraft {
  multi: boolean;
  firstName: string;
}

export interface HandoverDraft {
  checkIn: string;
  checkOut: string;
}

export const DEFAULT_HANDOVER: HandoverDraft = { checkIn: '15:00', checkOut: '12:00' };
export const DEFAULT_SITTING_MIN = 90;

export interface InviteLookup extends PublicInvite {
  token: string;
}

interface State {
  intent: Intent;
  /** Member: the code the person brought (looked up, public) — null on «تابع برقمك». */
  invite: InviteLookup | null;
  draft: BusinessDraft;
  /**
   * The person's own name as profile has it (business_identities.full_name,
   * «ما اسمك؟»): read after the PIN, written by the you-screen. '' = none yet.
   * Not part of the business draft — it is the person's, one per identity.
   */
  ownerName: string;
  hours: HoursDraft;
  /** The chosen category row (its default mode drives the shape sentence + scheduling write). */
  type: BusinessType | null;
  /** The public-page handle profile suggested for the name (then the created business's own). */
  slug: string | null;
  /** Created by ensureBusiness (data/enroll.ts); kept so a retry never creates twice. */
  createdId: string | null;
  /**
   * The writing step finished: the scheduling PUT, the first branch's name AND
   * the step's own write (hours / work days) all succeeded. A business with
   * createdId and no `scheduled` goes back to its writing screen.
   */
  scheduled: boolean;
  /** The shape step finished with its minimum (R8; staff may skip) — Done waits for it. */
  shapeDone: boolean;
  /** «عندك أكثر من فرع؟» — the switch and the first branch's name (board 26 enroll/branches--v2). */
  branches: BranchesDraft;
  /** The first branch's name was written onto profile's main branch (once). */
  branchesApplied: boolean;
  /** Shape 4's handover times ('HH:MM'), board 22 defaults ٣:٠٠ م / ١٢:٠٠ م. */
  handover: HandoverDraft;
  /** Shape 6's default sitting minutes (ruling W2, default ٩٠). */
  sittingMin: number;
  /** erp-core was asked to mirror the branches (done screen, best effort, once — ruling R19). */
  erpSynced: boolean;
  /** The memberships the post-PIN claim returned. */
  memberships: ClaimedMembership[] | null;
  /** Team invites sent during the shape step. */
  invites: TeamInvite[];
}

const EMPTY_DRAFT: BusinessDraft = { name: '', category: '', city: '', area: '', phone: '' };
const DRAFT_KEY = 'erp.onboarding.draft';

type Persisted = Pick<State, 'intent' | 'invite' | 'draft' | 'ownerName' | 'hours' | 'type' | 'slug' | 'createdId' | 'scheduled' | 'shapeDone' | 'branches' | 'branchesApplied' | 'handover' | 'sittingMin' | 'erpSynced'> & {
  /** The pre-1E key for erpSynced (a draft saved before the mirror): read as an alias. */
  tillCreated?: boolean;
};

function fresh(): State {
  return {
    intent: 'owner',
    invite: null,
    draft: { ...EMPTY_DRAFT },
    ownerName: '',
    hours: { days: DEFAULT_HOURS.days.map((d) => ({ ...d })) },
    type: null,
    slug: null,
    createdId: null,
    scheduled: false,
    shapeDone: false,
    branches: { multi: false, firstName: '' },
    branchesApplied: false,
    handover: { ...DEFAULT_HANDOVER },
    sittingMin: DEFAULT_SITTING_MIN,
    erpSynced: false,
    memberships: null,
    invites: [],
  };
}

function load(): State {
  const base = fresh();
  const saved = readJson<Partial<Persisted>>(DRAFT_KEY);
  if (!saved || typeof saved !== 'object') return base;
  const days = Array.isArray(saved.hours?.days) && saved.hours.days.length === 7 ? saved.hours.days : base.hours.days;
  return {
    ...base,
    intent: saved.intent === 'member' ? 'member' : 'owner',
    invite: saved.invite && typeof saved.invite.token === 'string' ? saved.invite : null,
    draft: { ...base.draft, ...(saved.draft ?? {}) },
    ownerName: typeof saved.ownerName === 'string' ? saved.ownerName : '',
    hours: { days: days.map((d) => ({ open: !!d.open, from: String(d.from ?? '09:00'), to: String(d.to ?? '21:00') })) },
    type: saved.type ?? null,
    slug: typeof saved.slug === 'string' ? saved.slug : null,
    createdId: typeof saved.createdId === 'string' ? saved.createdId : null,
    scheduled: !!saved.scheduled,
    shapeDone: !!saved.shapeDone,
    branches: { multi: !!saved.branches?.multi, firstName: typeof saved.branches?.firstName === 'string' ? saved.branches.firstName : '' },
    branchesApplied: !!saved.branchesApplied,
    handover: {
      checkIn: typeof saved.handover?.checkIn === 'string' ? saved.handover.checkIn : DEFAULT_HANDOVER.checkIn,
      checkOut: typeof saved.handover?.checkOut === 'string' ? saved.handover.checkOut : DEFAULT_HANDOVER.checkOut,
    },
    sittingMin: typeof saved.sittingMin === 'number' && saved.sittingMin > 0 ? saved.sittingMin : DEFAULT_SITTING_MIN,
    erpSynced: !!(saved.erpSynced ?? saved.tillCreated),
  };
}

function persist(s: State) {
  const p: Persisted = {
    intent: s.intent,
    invite: s.invite,
    draft: s.draft,
    ownerName: s.ownerName,
    hours: s.hours,
    type: s.type,
    slug: s.slug,
    createdId: s.createdId,
    scheduled: s.scheduled,
    shapeDone: s.shapeDone,
    branches: s.branches,
    branchesApplied: s.branchesApplied,
    handover: s.handover,
    sittingMin: s.sittingMin,
    erpSynced: s.erpSynced,
  };
  writeStore(DRAFT_KEY, JSON.stringify(p));
}

let state: State = load();
let enrollment: Enrollment | null = null;
const listeners = new Set<() => void>();

/** Loaded only in a mock build: the fake never reaches a production bundle's main chunk. */
const mockAuthentikFetch: typeof fetch = async (input, init) => (await import('./mock/authentik')).mockAuthentik().fetch(input, init);

function emit() {
  persist(state);
  listeners.forEach((l) => l());
}

export const onboardingStore = {
  get: (): State => state,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  set(patch: Partial<State>): void {
    state = { ...state, ...patch };
    emit();
  },
  setDraft(patch: Partial<BusinessDraft>): void {
    state = { ...state, draft: { ...state.draft, ...patch } };
    emit();
  },
  /** The branches answer (the switch and the first branch's name); not applied until ensureBusiness. */
  setBranches(patch: Partial<BranchesDraft>): void {
    state = { ...state, branches: { ...state.branches, ...patch }, branchesApplied: false };
    emit();
  },
  setHandover(patch: Partial<HandoverDraft>): void {
    state = { ...state, handover: { ...state.handover, ...patch } };
    emit();
  },
  setDay(i: number, patch: Partial<DayDraft>): void {
    state = { ...state, hours: { days: state.hours.days.map((d, j) => (j === i ? { ...d, ...patch } : d)) } };
    emit();
  },
  /**
   * The one Enrollment of this wizard. Online it drives the real Authentik
   * (same-origin proxy); under the mock interceptor it drives the SAME client
   * against the scripted fake Authentik (mock/authentik.ts).
   */
  enrollment(): Enrollment {
    if (!enrollment) enrollment = createEnrollment(runtimeFlags.mocks ? { fetch: mockAuthentikFetch } : {});
    return enrollment;
  },
  /** Tests: a specific transport for the next enrollment. */
  useEnrollment(e: Enrollment | null): void {
    enrollment = e;
  },
  reset(): void {
    enrollment?.reset();
    enrollment = null;
    state = fresh();
    writeStore(DRAFT_KEY, null);
    listeners.forEach((l) => l());
  },
  /** Tests: re-read the persisted draft (what a reload would do). */
  reload(): void {
    state = load();
    listeners.forEach((l) => l());
  },
};

export function useOnboarding(): State {
  return useSyncExternalStore(onboardingStore.subscribe, onboardingStore.get, onboardingStore.get);
}
